/**
 * BrandLogo — organization-aware brand mark. Shows the org's uploaded logo,
 * the founding GADTECH mark for the legacy org, or the ArmorBatch Global wordmark.
 */
import React from "react";
import { Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BACKEND_ORIGIN } from "@/lib/api";

export const ArmorBatchWordmark = ({ className = "h-10", ...rest }) => (
  <div className={`flex items-center gap-2 ${className}`} data-testid="armorbatch-wordmark" {...rest}>
    <Shield className="h-[70%] w-auto text-primary-orange" strokeWidth={2.5} />
    <div className="leading-none">
      <div className="heading font-black uppercase tracking-tight text-[1.05em] text-ink">
        ARMOR<span className="text-primary-orange">BATCH</span>
      </div>
      <div className="mono text-[0.5em] uppercase tracking-[0.35em] text-muted-industrial">Global</div>
    </div>
  </div>
);

export const GadtechLogo = ({ className = "h-10", slogan = false, variant = "inline", alt, ...rest }) => {
  const auth = useAuth();
  const org = auth?.org;
  let src = null;
  if (org?.logo_url) src = org.logo_url.startsWith("/api") ? `${BACKEND_ORIGIN}${org.logo_url}` : org.logo_url;
  else if (!org || org.id === "org_gadtech") src = "/images/gadtech-logo.png";

  if (!src) return <ArmorBatchWordmark className={className} {...rest} />;

  const img = (
    <img
      src={src}
      alt={alt || org?.name || "ArmorBatch Global"}
      draggable={false}
      className={`${className} w-auto object-contain select-none`}
      data-testid="gadtech-logo"
      {...rest}
    />
  );
  if (variant === "stack" || slogan) {
    return (
      <div className="flex flex-col items-start leading-none">
        {img}
        <span className="text-[9px] italic tracking-wide text-primary-orange/80 mt-0.5" data-testid="gadtech-logo-slogan">
          {org?.company?.tagline ? `${org.name} · ${org.company.tagline}` : "Powered by ArmorBatch Global"}
        </span>
      </div>
    );
  }
  return img;
};

export default GadtechLogo;
